"use client";

import { useState } from "react";
import {
  ErrorNotice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { CagrResults } from "@/components/tools/econ-macro/cagr-results";
import { MACRO_CHART_STRINGS } from "@/components/tools/econ-macro/line-figure";
import { useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type GrowthError,
  type GrowthMode,
  solveGrowth,
} from "@/lib/tool-kit/econ-macro/cagr";

/** English strings. A translated content file overrides these through `ui`. */
export const CAGR_CALCULATOR_STRINGS = {
  ...MACRO_CHART_STRINGS,
  modeLabel: "What do you want to find?",
  modeRate: "The growth rate (CAGR) between two values",
  modeFuture: "The ending value, projecting forward at a rate",
  modeYears: "The time needed to reach a target",
  inputsHeading: "Your numbers",
  beginField: "Beginning value",
  endField: "Ending value",
  targetField: "Target value",
  rateField: "Growth rate per year (%)",
  yearsField: "Number of years",
  yearsHint: "Fractions work: 2.5 means two and a half years.",
  timeLabel: "The time span is given as",
  timeYears: "A number of years",
  timeDates: "A start year and an end year",
  startYear: "Start year",
  endYear: "End year",
  datesHint: "The span is the end year minus the start year.",
  errMissing: "Fill in every field with a number.",
  errValue:
    "Values must be above zero. A compound rate is a ratio, so it cannot start from or reach zero.",
  errYears: "Enter a time span above zero and no more than 1000 years.",
  errRate: "The growth rate must be above −100%.",
  errSame:
    "The beginning and target values are equal, so no time is needed. Enter a different target.",
  errUnreachable:
    "At this rate the value never reaches the target. Growth must be positive to climb and negative to fall, and the answer must be within 1000 years.",
  resultsHeading: "Results",
  cagrCard: "Compound annual growth rate",
  endingCard: "Ending value",
  yearsCard: "Years needed",
  totalGrowthCard: "Total growth",
  multipleCard: "Growth multiple",
  simpleCard: "Simple average per year",
  doublingCard: "Doubling time (years)",
  gainCard: "Gain over the period",
  noDoubling: "—",
  workingHeading: "Working",
  workCagr:
    "CAGR = (ending ÷ beginning)^(1 ÷ years) − 1 = ({end} ÷ {begin})^(1 ÷ {years}) − 1 = {rate}",
  workFuture:
    "Ending value = beginning × (1 + rate)^years = {begin} × (1 + {rateDecimal})^{years} = {end}",
  workYears:
    "Years = ln(target ÷ beginning) ÷ ln(1 + rate) = ln({end} ÷ {begin}) ÷ ln(1 + {rateDecimal}) = {years}",
  workTotal:
    "Total growth = ending ÷ beginning − 1 = {end} ÷ {begin} − 1 = {total}",
  workSimple:
    "Simple average = total growth ÷ years = {total} ÷ {years} = {simple}",
  workDoubling: "Doubling time = ln 2 ÷ ln(1 + rate) = {doubling} years",
  pathHeading: "The smooth path",
  yearColumn: "Year",
  valueColumn: "Value at this rate",
  growthColumn: "Growth since the start",
  seriesValue: "Value at a constant rate",
  chartTitle: "Growth at a constant yearly rate",
  chartXTitle: "Years",
  chartYTitle: "Value",
  chartDescription:
    "A line graph of a value growing from {begin} to {end} over {years} years at a constant yearly rate of {rate}.",
  smoothNote:
    "CAGR smooths the path. It says nothing about the ups and downs along the way, and it is not the same as the average of the yearly growth rates. Compare it only over the same start and end points.",
} as const;

export type Strings = typeof CAGR_CALCULATOR_STRINGS;
type TimeKind = "years" | "dates";

const ERRORS: Record<GrowthError, keyof Strings> = {
  missing: "errMissing",
  "bad-value": "errValue",
  "bad-years": "errYears",
  "bad-rate": "errRate",
  "same-value": "errSame",
  unreachable: "errUnreachable",
};

const START = {
  rate: { begin: "10000", end: "25000", rate: "5", years: "8" },
  future: { begin: "1000", end: "", rate: "5", years: "10" },
  years: { begin: "1000", end: "2000", rate: "5", years: "" },
} as const;

export default function CagrCalculatorWidget() {
  const strings = useToolUi(CAGR_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<GrowthMode>("rate");
  const [begin, setBegin] = useState<string>(START.rate.begin);
  const [end, setEnd] = useState<string>(START.rate.end);
  const [rate, setRate] = useState<string>(START.future.rate);
  const [years, setYears] = useState<string>(START.rate.years);
  const [timeKind, setTimeKind] = useState<TimeKind>("years");
  const [startYear, setStartYear] = useState("2017");
  const [endYear, setEndYear] = useState("2025");

  const changeMode = (next: GrowthMode) => {
    setMode(next);
    setBegin(START[next].begin);
    setEnd(START[next].end);
    setRate(START[next].rate);
    setYears(START[next].years);
  };

  const needsTime = mode !== "years";
  const needsEnd = mode !== "future";
  const needsRate = mode !== "rate";
  let span = parseNumberInput(years);
  if (needsTime && timeKind === "dates") {
    const from = parseNumberInput(startYear);
    const to = parseNumberInput(endYear);
    span = from === null || to === null ? null : to - from;
  }
  const rateValue = parseNumberInput(rate);
  const solution = solveGrowth(mode, {
    begin: parseNumberInput(begin),
    end: needsEnd ? parseNumberInput(end) : null,
    rate: needsRate && rateValue !== null ? rateValue / 100 : null,
    years: needsTime ? span : null,
  });
  const problem =
    typeof solution === "string" ? strings[ERRORS[solution]] : null;

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <SelectField
          id="cagr-mode"
          label={strings.modeLabel}
          onChange={changeMode}
          options={[
            { value: "rate", label: strings.modeRate },
            { value: "future", label: strings.modeFuture },
            { value: "years", label: strings.modeYears },
          ]}
          value={mode}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="cagr-begin"
            label={strings.beginField}
            numeric
            onChange={setBegin}
            value={begin}
          />
          {needsEnd ? (
            <TextField
              id="cagr-end"
              label={mode === "years" ? strings.targetField : strings.endField}
              numeric
              onChange={setEnd}
              value={end}
            />
          ) : null}
          {needsRate ? (
            <TextField
              id="cagr-rate"
              label={strings.rateField}
              numeric
              onChange={setRate}
              value={rate}
            />
          ) : null}
        </div>
        {needsTime ? (
          <div className="space-y-4">
            <PillGroup
              label={strings.timeLabel}
              name="cagr-time"
              onChange={setTimeKind}
              options={[
                { value: "years", label: strings.timeYears },
                { value: "dates", label: strings.timeDates },
              ]}
              value={timeKind}
            />
            {timeKind === "years" ? (
              <TextField
                hint={strings.yearsHint}
                id="cagr-years"
                label={strings.yearsField}
                numeric
                onChange={setYears}
                value={years}
              />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  hint={strings.datesHint}
                  id="cagr-start"
                  label={strings.startYear}
                  numeric
                  onChange={setStartYear}
                  value={startYear}
                />
                <TextField
                  id="cagr-end-year"
                  label={strings.endYear}
                  numeric
                  onChange={setEndYear}
                  value={endYear}
                />
              </div>
            )}
          </div>
        ) : null}
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {typeof solution === "string" ? null : (
          <CagrResults mode={mode} solution={solution} strings={strings} />
        )}
      </section>
    </div>
  );
}
